// deps
import React, { memo, useMemo, RefObject } from "react";
import classNames from "classnames";

//internal deps
import { ScreenReaderOnly, AriaHidden, isEmpty } from "@swiggy-private/core-ui";

// components
import Header from "./components/HeaderV3";
import BenefitList from "./components/BenefitListV3";
import PurchasePlan from "./components/PurchasePlanV3";
import SelectedPlan from "./components/SelectedPlanV3";
import { OneLogo } from "@super-ui/widgets/swiggy-one-logo";

// types
import { RenderingDetails, SubsItem } from "@swiggy-private/cart-types";
import { ONE_WIDGET_TEST_IDS } from "@super-ui/test-ids/one-widget";
import { DeliveryTagInfoUIDataMweb } from "@super-ui/interfaces";

// helpers
import { getPrefixedTestId } from "@super-ui/helpers/String";
import {
    shouldShowTitleShimmer,
    isTrialPlan,
    getOfferBadgeText,
    isOneLiteProgramAndType,
    colorPickerBasedOnVisibilityLevel,
    getDeliveryTagInfoData,
} from "@super-ui/helpers/OneWidget";

import styles from "./styles/SwiggyOneWidget.v3.scss";

export interface SwiggyOneWidgetProps {
    itemData: RenderingDetails;
    subscriptionItems?: SubsItem[];
    onItemClick?: (itemType: string, itemData?: RenderingDetails) => void;
    removeSuper: (planName: string, planPrice: string) => void;
    changeSuper: (planPrice: string, plan?: SubsItem) => void;
    changeSuperBtnRef: RefObject<HTMLButtonElement>;
    impressionSuperNudge: (title: string, ctaText: string, subtitle?: string) => void;
    impressionSuperChangePlan: (planName: string, planPrice: string, itemData: RenderingDetails) => void;
    impressionSuperOfferBadge: (offerText: string) => void;
    testIdPrefix?: string;
}

const SwiggyOneWidget: React.FC<SwiggyOneWidgetProps> = ({
    itemData,
    subscriptionItems = [],
    onItemClick,
    removeSuper,
    changeSuper,
    changeSuperBtnRef,
    impressionSuperNudge,
    impressionSuperChangePlan,
    impressionSuperOfferBadge,
    testIdPrefix,
}: SwiggyOneWidgetProps) => {
    const { meta, display_text: displayText, key } = itemData;
    const { benefits = [], logo_ctx: logoCtx, delivery_tag_info: deliveryTagInfo = null } = meta || {};
    const showSubscription = useMemo(() => subscriptionItems.length !== 0, [subscriptionItems]);
    const containerTestId = useMemo(() => getPrefixedTestId(ONE_WIDGET_TEST_IDS.CONTAINER, testIdPrefix), [
        testIdPrefix,
    ]);
    const isOneTrialPlan = useMemo(() => isTrialPlan(meta), [meta]);
    const isOneLitePlan = useMemo(() => isOneLiteProgramAndType(meta), [meta]);
    const visibilityLevelConfig = useMemo(() => colorPickerBasedOnVisibilityLevel(deliveryTagInfo?.visibility_level), [
        deliveryTagInfo,
    ]);
    const deliveryTagConfig = useMemo(
        () => (isOneLitePlan ? (getDeliveryTagInfoData(meta, true) as DeliveryTagInfoUIDataMweb) : null),
        [isOneLitePlan, meta],
    );
    const offerBadgeText = useMemo(() => getOfferBadgeText(meta), [meta]);
    const showTitleShimmer = useMemo(() => shouldShowTitleShimmer(key, isOneTrialPlan), [key, isOneTrialPlan]);

    const mainContainerClass = classNames({
        [styles.mainContainer]: true,
        [styles.mainContainerWithConfetti]: isOneTrialPlan,
        [styles.mainContainerOneLiteHigh]: visibilityLevelConfig?.isHigh,
        [styles.mainContainerOneLiteLow]: visibilityLevelConfig?.isLow,
    });

    const logoContainerClass = classNames({
        [styles.logo]: true,
        [styles.logoSmall]: !showSubscription,
        [styles.logoBig]: showSubscription,
    });

    return (
        <>
            <div className={mainContainerClass} data-testid={containerTestId}>
                {isEmpty(offerBadgeText) && !isOneLitePlan && (
                    <AriaHidden as="div" className={logoContainerClass}>
                        <OneLogo />
                    </AriaHidden>
                )}
                <div className={styles.topContainer}>
                    <ScreenReaderOnly text={displayText} />
                    <Header
                        title={displayText}
                        showTitleShimmer={showTitleShimmer}
                        logoCtx={logoCtx}
                        testIdPrefix={testIdPrefix}
                        isOneLitePlan={isOneLitePlan}
                        deliveryTagConfig={deliveryTagConfig}
                    />
                </div>

                <BenefitList benefits={benefits} testIdPrefix={testIdPrefix} />

                <PurchasePlan
                    itemData={itemData}
                    onItemClick={onItemClick}
                    testIdPrefix={testIdPrefix}
                    offerBadgeText={offerBadgeText}
                    impressionSuperNudge={impressionSuperNudge}
                    impressionSuperOfferBadge={impressionSuperOfferBadge}
                />
            </div>
            {showSubscription && (
                <SelectedPlan
                    itemData={itemData}
                    subscriptionItem={subscriptionItems[0]}
                    removeSuper={removeSuper}
                    changeSuper={changeSuper}
                    changeSuperBtnRef={changeSuperBtnRef}
                    impressionSuperChangePlan={impressionSuperChangePlan}
                    testIdPrefix={testIdPrefix}
                />
            )}
        </>
    );
};

export default memo(SwiggyOneWidget);
